// The `briefField` node: one titled section of an interview brief.
//
// A brief's document is `doc > heading > paragraph > briefField*`. Each field
// carries its identity and audience as node attributes, so autosave, version
// snapshots, comment anchoring, diff and the client projection all keep
// working with no new plumbing (see src/shared/briefFields.ts).
//
// Three things live in this file:
//   1. the node type and its plain-DOM node view (header row, audience chip,
//      ⋯ menu), built the same way as ScriptCell/CellTypeChip so it renders
//      identically in the editable admin editor and the read-only client one;
//   2. a structural guard plugin — the only way to add, remove or reorder a
//      field is through the commands below, so select-all + Delete and
//      pasting a copied field are inert;
//   3. the commands themselves, thin wrappers over the pure transaction
//      builders in briefFieldCommands.ts.

// Imported under an alias: this file also handles DOM `Node` values (mutation
// targets, event targets), and the two names would otherwise collide.
import { Node as TiptapNode } from '@tiptap/core';
import type { Node as PMNode, ResolvedPos } from '@tiptap/pm/model';
import { Plugin, PluginKey, TextSelection, type Transaction } from '@tiptap/pm/state';
import { ulid } from '../../../shared/ulid';
import { exceedsWarmupLimit } from '../../../shared/warmupBank';
import {
  BRIEF_AUDIENCE_META,
  BRIEF_FIELD_NODE,
  CUSTOM_FIELD_KEY,
  isBriefAudience,
  isBriefBodyKind,
  isBuiltInFieldKey,
  normalizeBriefFieldAttrs,
  type BriefAudience,
  type BriefFieldAttrs,
} from '../../../shared/briefFields';
import { AUDIENCE_CARET, AUDIENCE_CHECK, AUDIENCE_ICONS } from '../Brief/audienceIcons';
import { showToast } from '../Toaster';
import {
  BRIEF_STRUCTURAL_META,
  addCustomBriefFieldTr,
  briefFieldIds,
  deleteBriefFieldTr,
  findBriefField,
  insertBriefFieldAtIndexTr,
  moveBriefFieldToTr,
  moveBriefFieldTr,
  patchBriefFieldAttrsTr,
  setBriefWarmupsTr,
  topLevelBriefFields,
} from './briefFieldCommands';

export { BRIEF_STRUCTURAL_META } from './briefFieldCommands';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    briefField: {
      /** Patch a field's attrs. Built-ins ignore `title` and `key`. */
      setBriefFieldAttrs: (id: string, patch: Partial<BriefFieldAttrs>) => ReturnType;
      /** Swap with the adjacent block, only when that block is a field too. */
      moveBriefField: (id: string, direction: 'up' | 'down') => ReturnType;
      /** Drop a field immediately before or after another one. */
      moveBriefFieldTo: (
        id: string,
        targetId: string,
        placement: 'before' | 'after',
      ) => ReturnType;
      /** Append a switched-on custom field and put the cursor inside it. */
      addCustomBriefField: (input: { title: string; audience: BriefAudience }) => ReturnType;
      /** Remove a custom field. Refuses built-ins. */
      deleteBriefField: (id: string) => ReturnType;
      showBriefField: (id: string) => ReturnType;
      hideBriefField: (id: string) => ReturnType;
      /** Plain warm-ups only, at most three; also works on older briefs. */
      setBriefWarmups: (texts: readonly string[]) => ReturnType;
    };
  }
}

// ---------------------------------------------------------------------------
// Structural guard
// ---------------------------------------------------------------------------

export const briefFieldGuardKey = new PluginKey('briefFieldGuard');

/**
 * prosemirror-history's plugin key. `new PluginKey('history')` resolves to the
 * string `'history$'`, and undo/redo tag their transactions with it — the only
 * handle we have on "this is history replaying a step it recorded earlier".
 */
export const HISTORY_META = 'history$';

/**
 * Rejects any transaction that would add, remove or duplicate a `briefField`
 * unless it carries BRIEF_STRUCTURAL_META — i.e. unless it came from one of
 * the commands below.
 *
 * The effect on a brief: select-all + Delete and pasting a copied field do
 * nothing, so the client (and a stray keystroke) cannot break the field set
 * the projection and the PDF depend on. Everything else — typing, Enter,
 * lists, insertScriptRowAfter inside a `rows` field — is untouched, because
 * none of it changes the multiset of field ids.
 *
 * Undo and redo are the third way through. prosemirror-history replays the
 * inverse of a step it already recorded, so it can legitimately put a deleted
 * field back or take an added one away, and its transactions carry no
 * structural meta of ours. They are recognised by the history plugin's own
 * meta key instead.
 *
 * The comparison is between the two id *multisets*, not a uniqueness check on
 * the result. A document that somehow already holds two fields with one id
 * has to stay editable — a guard that rejected every keystroke because of a
 * pre-existing duplicate would freeze the document, and reloading would not
 * help. Duplicates are healed below instead, not defended against here.
 */
export function briefFieldGuardPlugin({ healShoot = true }: { healShoot: boolean } = { healShoot: true }): Plugin {
  return new Plugin({
    key: briefFieldGuardKey,
    filterTransaction(tr, state) {
      if (!tr.docChanged) return true;
      if (exceedsWarmupLimit(tr.doc.toJSON())) {
        if (typeof window !== 'undefined') showToast('error', 'Keep warm-ups to three questions or fewer.');
        return false;
      }
      if (tr.getMeta(BRIEF_STRUCTURAL_META) === true) return true;
      if (tr.getMeta(HISTORY_META) !== undefined) return true;

      const before = briefFieldIds(state.doc);
      const after = briefFieldIds(tr.doc);
      // Ordinary scripts have no fields at all — nothing to police.
      if (before.length === 0 && after.length === 0) return true;
      if (!sameFieldIds(before, after)) return false;
      // Fields live at the top level. Wrapping them (Cmd+A then blockquote)
      // keeps every id but drops them a level, where the drawer, the menu and
      // the anchor mapping cannot see them. Reject any transaction that nests
      // a field that was not already nested.
      const nestedBefore = before.length - topLevelBriefFields(state.doc).length;
      const nestedAfter = after.length - topLevelBriefFields(tr.doc).length;
      return nestedAfter <= nestedBefore;
    },

    /**
     * The repair pass, run after every transaction the state accepts. It fixes
     * the three states the rest of the editor cannot express safely:
     *
     *   1. a field with a blank or duplicated id — from a doc written by an
     *      older build, or from both undo routes being taken for one delete —
     *      gets a fresh ULID, so ids are unique again and the guard's multiset
     *      comparison has something stable to work with;
     *   2. a selection inside a switched-off field. A hidden field draws no
     *      body at all, so a caret in one is invisible and everything typed
     *      lands in content nobody can see. Autofocus at the end of a seeded
     *      brief does exactly that, since the library's last field is off;
     *   3. a field with no shoot attr from a doc written before the shoot view
     *      gets its per-key default written onto the node.
     */
    appendTransaction(_transactions, _oldState, newState) {
      const tr = newState.tr;
      const healed = healFieldIds(tr);
      const healedShoot = healShoot && healShootAttrs(tr);
      const moved = relocateOutOfHiddenField(tr);
      if (!healed && !healedShoot && !moved) return null;
      tr.setMeta(BRIEF_STRUCTURAL_META, true);
      // A repair is not an edit the user made: keeping it out of history
      // stops Cmd+Z from reverting a re-id only for the plugin to re-apply
      // it, which would pin the undo stack in place.
      tr.setMeta('addToHistory', false);
      return tr;
    },
  });
}

/** True when two id lists hold the same ids the same number of times. */
function sameFieldIds(before: string[], after: string[]): boolean {
  if (before.length !== after.length) return false;
  const counts = new Map<string, number>();
  for (const id of before) counts.set(id, (counts.get(id) ?? 0) + 1);
  for (const id of after) {
    const left = counts.get(id);
    if (!left) return false;
    counts.set(id, left - 1);
  }
  return true;
}

/** Give every blank or repeated field id a fresh ULID. */
function healFieldIds(tr: Transaction): boolean {
  const seen = new Set<string>();
  const rewrites: Array<{ pos: number; node: PMNode }> = [];
  tr.doc.descendants((node, pos) => {
    if (node.type.name !== BRIEF_FIELD_NODE) return !node.isTextblock;
    const id = typeof node.attrs.id === 'string' ? node.attrs.id : '';
    if (!id || seen.has(id)) rewrites.push({ pos, node });
    else seen.add(id);
    return false;
  });
  // setNodeMarkup keeps the node's size, so positions gathered up front stay
  // valid across the rewrites.
  for (const { pos, node } of rewrites) {
    let id = ulid();
    while (seen.has(id)) id = ulid();
    seen.add(id);
    tr.setNodeMarkup(pos, undefined, { ...node.attrs, id });
  }
  return rewrites.length > 0;
}

/** Persist per-key shoot defaults for documents written before the attribute. */
function healShootAttrs(tr: Transaction): boolean {
  const rewrites: Array<{ pos: number; node: PMNode }> = [];
  tr.doc.descendants((node, pos) => {
    if (node.type.name !== BRIEF_FIELD_NODE) return !node.isTextblock;
    if (typeof node.attrs.shoot !== 'boolean') rewrites.push({ pos, node });
    return false;
  });
  // setNodeMarkup keeps node size, so the gathered positions remain valid.
  for (const { pos, node } of rewrites) {
    tr.setNodeMarkup(pos, undefined, {
      ...node.attrs,
      shoot: normalizeBriefFieldAttrs(node.attrs).shoot,
    });
  }
  return rewrites.length > 0;
}

/** The switched-off field the position sits in, if it is in one at all. */
function hiddenFieldAround($pos: ResolvedPos): { start: number; end: number } | null {
  for (let depth = $pos.depth; depth > 0; depth--) {
    const node = $pos.node(depth);
    if (node.type.name !== BRIEF_FIELD_NODE) continue;
    if (normalizeBriefFieldAttrs(node.attrs).enabled) return null;
    return { start: $pos.before(depth), end: $pos.after(depth) };
  }
  return null;
}

/** A position inside each textblock the reader can actually see. */
function visibleTextblocks(doc: PMNode): number[] {
  const out: number[] = [];
  doc.descendants((node, pos) => {
    if (node.type.name === BRIEF_FIELD_NODE) {
      return normalizeBriefFieldAttrs(node.attrs).enabled;
    }
    if (node.isTextblock) {
      out.push(pos + 1);
      return false;
    }
    return true;
  });
  return out;
}

/**
 * Put the caret back somewhere it can be seen: the first visible textblock
 * after the hidden field, else the last one before it.
 */
function relocateOutOfHiddenField(tr: Transaction): boolean {
  const field =
    hiddenFieldAround(tr.selection.$from) ?? hiddenFieldAround(tr.selection.$to);
  if (!field) return false;

  const visible = visibleTextblocks(tr.doc);
  if (visible.length === 0) return false;
  const after = visible.find((pos) => pos >= field.end);
  const before = [...visible].reverse().find((pos) => pos <= field.start);
  const target = after ?? before ?? visible[visible.length - 1];

  tr.setSelection(TextSelection.near(tr.doc.resolve(target)));
  return true;
}

// ---------------------------------------------------------------------------
// The node
// ---------------------------------------------------------------------------

export interface BriefFieldOptions {
  /** Build the audience chip and the field menu. Off for the client's
   *  suggest-mode editor. */
  controls: boolean;
}

export const BriefField = TiptapNode.create<BriefFieldOptions>({
  name: BRIEF_FIELD_NODE,
  group: 'block',
  // Fields never nest: `briefField` is deliberately absent from this list.
  content: '(paragraph | heading | bulletList | orderedList | scriptHeaderRow | scriptRow)+',
  isolating: true,
  defining: true,
  selectable: false,
  draggable: false,

  addOptions() {
    return { controls: true };
  },

  addAttributes() {
    return {
      id: {
        default: '',
        parseHTML: (el) => el.getAttribute('data-id') ?? '',
        renderHTML: (attrs: { id?: string }) => ({ 'data-id': attrs.id ?? '' }),
      },
      key: {
        default: CUSTOM_FIELD_KEY,
        parseHTML: (el) => el.getAttribute('data-key') ?? CUSTOM_FIELD_KEY,
        renderHTML: (attrs: { key?: string }) => ({ 'data-key': attrs.key ?? CUSTOM_FIELD_KEY }),
      },
      title: {
        default: '',
        parseHTML: (el) => el.getAttribute('data-title') ?? '',
        renderHTML: (attrs: { title?: string }) => ({ 'data-title': attrs.title ?? '' }),
      },
      // Meaningful only on the `talent` field: the call sheet names this
      // brief's synced questions and its Docs row after it.
      talent_name: {
        default: '',
        parseHTML: (el) => el.getAttribute('data-talent-name') ?? '',
        renderHTML: (attrs: { talent_name?: string }) => ({
          'data-talent-name': attrs.talent_name ?? '',
        }),
      },
      audience: {
        default: 'internal',
        parseHTML: (el) => {
          const raw = el.getAttribute('data-audience');
          return isBriefAudience(raw) ? raw : 'internal';
        },
        renderHTML: (attrs: { audience?: unknown }) => ({
          'data-audience': isBriefAudience(attrs.audience) ? attrs.audience : 'internal',
        }),
      },
      // Fails closed like normalizeBriefFieldAttrs: a field stored without an
      // `enabled` attr is hidden by the server projection, so the editor must
      // load it hidden too, or an autosave would flip it on for the client.
      enabled: {
        default: false,
        parseHTML: (el) => el.getAttribute('data-enabled') === 'true',
        renderHTML: (attrs: { enabled?: unknown }) => ({
          'data-enabled': String(attrs.enabled === true),
        }),
      },
      // Unlike `enabled`, the default depends on the sibling `key` (purpose and
      // key messages are on by default), and a ProseMirror attr default cannot
      // see its siblings. So the node default is null, normalizeBriefFieldAttrs
      // resolves null to defaultShoot(key), and the guard plugin's heal pass
      // writes the resolved value onto nodes that still carry null. If the
      // default were `false` here, every existing brief would load with purpose
      // and key messages off the phone and the first autosave would persist it.
      shoot: {
        default: null,
        parseHTML: (el) => {
          const raw = el.getAttribute('data-shoot');
          return raw === 'true' ? true : raw === 'false' ? false : null;
        },
        renderHTML: (attrs: { shoot?: unknown }) =>
          typeof attrs.shoot === 'boolean' ? { 'data-shoot': String(attrs.shoot) } : {},
      },
      body_kind: {
        default: 'rich',
        parseHTML: (el) => {
          const raw = el.getAttribute('data-body-kind');
          return isBriefBodyKind(raw) ? raw : 'rich';
        },
        renderHTML: (attrs: { body_kind?: unknown }) => ({
          'data-body-kind': isBriefBodyKind(attrs.body_kind) ? attrs.body_kind : 'rich',
        }),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-type="brief-field"]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      { 'data-type': 'brief-field', class: 'brief-field', ...HTMLAttributes },
      0,
    ];
  },

  addProseMirrorPlugins() {
    return [briefFieldGuardPlugin({ healShoot: this.options.controls })];
  },

  addNodeView() {
    // Captured out here: `this` is the extension inside addNodeView(), but not
    // inside the factory ProseMirror calls back per node.
    const options = this.options;
    return ({ node, editor }) => {
      let current: PMNode = node;
      let attrs = normalizeBriefFieldAttrs(current.attrs);
      // "Editable" for the purposes of the authoring chrome, which is not the
      // same as the body being typeable: the client's suggest-mode editor is
      // editable and configured with `controls: false`.
      const editable = editor.isEditable && options.controls;
      const hidden = !attrs.enabled;

      const dom = document.createElement('div');
      dom.setAttribute('data-type', 'brief-field');
      // Set once. ProseMirror owns classes on `dom` (selectednode, decorations),
      // so update() must never rewrite className — an `enabled` flip rebuilds
      // the whole view instead.
      dom.className = hidden ? 'brief-field brief-field--hidden' : 'brief-field';

      const syncData = (a: BriefFieldAttrs) => {
        dom.setAttribute('data-id', a.id);
        dom.setAttribute('data-key', a.key);
        dom.setAttribute('data-audience', a.audience);
        dom.setAttribute('data-enabled', String(a.enabled));
        dom.setAttribute('data-shoot', String(a.shoot));
        dom.setAttribute('data-body-kind', a.body_kind);
        dom.setAttribute('data-talent-name', a.talent_name);
      };
      syncData(attrs);

      // Hidden fields render nothing at all: no header, no body, no contentDOM.
      // The content stays in the document untouched — it is simply not drawn,
      // and comes back exactly as it was when the field is switched on.
      if (hidden) {
        return {
          dom,
          update(updated: PMNode) {
            if (updated.type.name !== BRIEF_FIELD_NODE) return false;
            const next = normalizeBriefFieldAttrs(updated.attrs);
            if (next.enabled) return false; // back on → rebuild with a body
            current = updated;
            attrs = next;
            syncData(next);
            return true;
          },
          ignoreMutation: () => true,
        };
      }

      // ---- header row -----------------------------------------------------
      const head = document.createElement('div');
      head.className = 'brief-field__head';
      head.setAttribute('contenteditable', 'false');

      const titleEl = document.createElement('span');
      titleEl.className = 'brief-field__title';
      titleEl.textContent = attrs.title;
      head.appendChild(titleEl);

      // Chip, Custom tag and ⋯ menu are authoring chrome, so they exist only
      // in an editable editor that asked for controls. Both client views opt
      // out: the read-only review page because it is read-only, and the
      // suggest-mode editor because `controls: false` (it is editable, but
      // flipping audience, hiding, renaming, deleting and reordering are
      // Henry's, and the server rehydrates every field's attrs from the master
      // doc anyway, so the controls would do nothing). A field is presented to
      // the client as a plain titled section (mock-up §06): the client never
      // receives a field that is not theirs, so an audience label there would
      // be noise, and "Custom" is a detail of how Henry built the brief. The
      // serif heading that replaces the mono eyebrow is CSS, scoped to the
      // [data-brief-view="client"] wrapper in styles.css; the admin's
      // read-only version preview keeps the editor look.
      const chip = editable ? document.createElement('button') : null;
      if (chip) {
        chip.className = `brief-aud brief-aud--${attrs.audience}`;
        chip.type = 'button';
        chip.setAttribute('aria-haspopup', 'menu');
        head.appendChild(chip);
      }

      let customTag: HTMLElement | null = null;
      const syncCustomTag = (a: BriefFieldAttrs) => {
        if (!chip) return;
        const wants = a.key === CUSTOM_FIELD_KEY;
        if (wants && !customTag) {
          customTag = document.createElement('span');
          customTag.className = 'brief-field__custom-tag';
          customTag.textContent = 'Custom';
          chip.insertAdjacentElement('afterend', customTag);
        } else if (!wants && customTag) {
          customTag.remove();
          customTag = null;
        }
      };
      syncCustomTag(attrs);

      let shootGlyph: HTMLElement | null = null;
      const syncShootGlyph = (a: BriefFieldAttrs) => {
        if (!chip) return;
        if (a.shoot && a.key !== 'questions') {
          if (!shootGlyph) {
            shootGlyph = document.createElement('span');
            shootGlyph.className = 'brief-field__shoot';
            shootGlyph.title = "On the shoot view's Brief tab";
            shootGlyph.setAttribute('aria-label', 'On the shoot view');
            shootGlyph.innerHTML = '<svg viewBox="0 0 16 16" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="4" y="1.5" width="8" height="13" rx="2"/><circle cx="8" cy="11.5" r="0.5"/></svg>';
            (customTag ?? chip).insertAdjacentElement('afterend', shootGlyph);
          }
        } else if (shootGlyph) {
          shootGlyph.remove();
          shootGlyph = null;
        }
      };
      syncShootGlyph(attrs);

      const renderChip = (a: BriefFieldAttrs) => {
        if (!chip) return;
        const meta = BRIEF_AUDIENCE_META[a.audience];
        chip.className = `brief-aud brief-aud--${a.audience}`;
        const locked = a.key === 'warmup';
        chip.disabled = locked;
        chip.title = locked ? 'Warm-ups are always internal.' : `${meta.label}. ${meta.gloss} Click to change.`;
        chip.setAttribute('aria-label', locked ? 'Audience: Internal. Warm-ups are always internal.' : `Audience: ${meta.label}. Change`);
        chip.innerHTML =
          `<span class="brief-aud__ic">${AUDIENCE_ICONS[a.audience]}</span>` +
          `<span class="brief-aud__label">${meta.label}</span>` +
          (locked ? '' : `<span class="brief-aud__caret">${AUDIENCE_CARET}</span>`);
      };
      renderChip(attrs);

      let dots: HTMLButtonElement | null = null;

      if (chip) {
        chip.addEventListener('mousedown', (e) => e.preventDefault());
        chip.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          openAudiencePicker();
        });

        dots = document.createElement('button');
        dots.type = 'button';
        dots.className = 'brief-field__dots';
        dots.setAttribute('aria-label', 'Field options');
        dots.setAttribute('aria-haspopup', 'menu');
        dots.title = 'Field options';
        dots.textContent = '⋯';
        dots.addEventListener('mousedown', (e) => e.preventDefault());
        dots.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          openFieldMenu();
        });
        head.appendChild(dots);
      }

      // ---- talent name ----------------------------------------------------
      // Only on the `talent` field. Producers get a button that opens an
      // inline input; every other view (read-only, client suggest mode) shows
      // the name as a plain label when one is set, and nothing when not.
      let talentEl: HTMLElement | null = null;
      let talentInput: HTMLInputElement | null = null;

      const syncTalent = (a: BriefFieldAttrs) => {
        const name = a.key === 'talent' ? a.talent_name : '';
        const wants = editable ? a.key === 'talent' : name !== '';
        if (!wants) {
          talentEl?.remove();
          talentEl = null;
          return;
        }
        if (!talentEl) {
          if (editable) {
            const btn = document.createElement('button');
            btn.type = 'button';
            btn.addEventListener('mousedown', (e) => e.preventDefault());
            btn.addEventListener('click', (e) => {
              e.preventDefault();
              e.stopPropagation();
              startTalentEdit();
            });
            talentEl = btn;
          } else {
            talentEl = document.createElement('span');
          }
          head.insertBefore(talentEl, dots);
        }
        talentEl.textContent = name || '+ Add talent name';
        if (!editable) {
          talentEl.className = 'brief-field__talent';
          talentEl.title = 'Talent';
          return;
        }
        talentEl.className = name
          ? 'brief-field__talent'
          : 'brief-field__talent brief-field__talent--empty';
        talentEl.title =
          "Names this brief's questions on the call sheet." +
          (a.audience === 'internal' ? ' Internal: the name stays off client links.' : '') +
          (name ? ' Click to edit.' : '');
        if (talentInput) talentInput.title = talentEl.title;
        talentEl.setAttribute('aria-label', name ? `Talent name: ${name}. Edit` : 'Add talent name');
      };
      syncTalent(attrs);

      dom.appendChild(head);

      const contentDOM = document.createElement('div');
      contentDOM.className = 'brief-field__body';
      dom.appendChild(contentDOM);

      // ---- the two menus --------------------------------------------------

      function openAudiencePicker() {
        if (!chip || attrs.key === 'warmup') return;
        openBriefMenu(chip, {
          align: 'left',
          head: 'Who sees this field',
          items: (['internal', 'client'] as BriefAudience[]).map((value) => ({
            label: BRIEF_AUDIENCE_META[value].label,
            gloss: BRIEF_AUDIENCE_META[value].gloss,
            checked: value === attrs.audience,
            onSelect: () => {
              editor.commands.setBriefFieldAttrs(attrs.id, { audience: value });
            },
          })),
        });
      }

      /** Whether the block above or below this field is another field. */
      function hasNeighbourField(direction: 'up' | 'down'): boolean {
        const doc = editor.state.doc;
        const found = findBriefField(doc, attrs.id);
        if (!found) return false;
        const index = direction === 'up' ? found.index - 1 : found.index + 1;
        if (index < 0 || index >= doc.childCount) return false;
        return doc.child(index).type.name === BRIEF_FIELD_NODE;
      }

      function openFieldMenu() {
        if (!dots) return;
        const builtIn = isBuiltInFieldKey(attrs.key);
        // Fields never move past the brief's heading and client line, so at
        // either end of the run the move is not on offer rather than silent.
        const canMoveUp = hasNeighbourField('up');
        const canMoveDown = hasNeighbourField('down');
        openBriefMenu(dots, {
          align: 'right',
          items: [
            {
              label: 'Move up',
              disabled: !canMoveUp,
              onSelect: () => editor.commands.moveBriefField(attrs.id, 'up'),
            },
            {
              label: 'Move down',
              disabled: !canMoveDown,
              onSelect: () => editor.commands.moveBriefField(attrs.id, 'down'),
            },
            {
              label: 'Hide',
              gloss: 'Keeps the content and drops it into the hidden strip.',
              onSelect: () => editor.commands.hideBriefField(attrs.id),
            },
            ...(attrs.key !== 'questions' ? [{
              label: 'Show in shoot view',
              gloss: 'On the Brief tab of the phone view.',
              checked: attrs.shoot,
              toggle: true,
              onSelect: () => editor.commands.setBriefFieldAttrs(attrs.id, { shoot: !attrs.shoot }),
            }] : []),
            { separator: true },
            {
              label: 'Rename',
              disabled: builtIn,
              hint: builtIn ? 'Custom only' : undefined,
              onSelect: startRename,
            },
            {
              label: 'Delete',
              disabled: builtIn,
              hint: builtIn ? 'Custom only' : undefined,
              danger: true,
              onSelect: deleteField,
            },
          ],
        });
      }

      // ---- inline rename --------------------------------------------------

      let renameInput: HTMLInputElement | null = null;

      function startRename() {
        if (renameInput) return;
        const input = document.createElement('input');
        input.type = 'text';
        input.className = 'brief-field__rename';
        input.value = attrs.title;
        input.setAttribute('aria-label', 'Field title');
        input.spellcheck = false;

        let settled = false;
        // `returnFocus` is only true for Enter and Escape. A blur means the
        // user has already clicked somewhere — often into the text — and
        // pulling focus onto the ⋯ button would take the caret back off them.
        const finish = (commit: boolean, returnFocus: boolean) => {
          if (settled) return;
          settled = true;
          const next = input.value.trim();
          input.remove();
          renameInput = null;
          titleEl.hidden = false;
          // An empty title reverts: a field with no name is unreadable in the
          // drawer, the hidden strip and the PDF alike.
          if (commit && next && next !== attrs.title) {
            editor.commands.setBriefFieldAttrs(attrs.id, { title: next });
          }
          if (returnFocus) dots?.focus();
        };

        input.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            finish(true, true);
          } else if (e.key === 'Escape') {
            e.preventDefault();
            finish(false, true);
          }
        });
        input.addEventListener('blur', () => finish(true, false));

        titleEl.hidden = true;
        renameInput = input;
        head.insertBefore(input, titleEl.nextSibling);
        input.focus();
        input.select();
      }

      // ---- inline talent name ---------------------------------------------
      // Same interaction as rename, except an empty value is allowed: clearing
      // the name is how a producer removes it.

      function startTalentEdit() {
        if (talentInput || !talentEl) return;
        const anchor = talentEl;
        const input = document.createElement('input');
        input.type = 'text';
        input.className = 'brief-field__talent-input';
        input.value = attrs.talent_name;
        const capInput = () => {
          input.value = Array.from(input.value).slice(0, 80).join('');
        };
        input.addEventListener('input', (event) => {
          if (!(event as InputEvent).isComposing) capInput();
        });
        input.addEventListener('compositionend', capInput);
        input.placeholder = 'Talent name';
        input.title = anchor.title;
        input.setAttribute('aria-label', 'Talent name');
        input.spellcheck = false;

        let settled = false;
        const finish = (commit: boolean, returnFocus: boolean) => {
          if (settled) return;
          settled = true;
          const next = Array.from(input.value.trim()).slice(0, 80).join('');
          input.remove();
          talentInput = null;
          anchor.hidden = false;
          if (commit && next !== attrs.talent_name) {
            editor.commands.setBriefFieldAttrs(attrs.id, { talent_name: next });
          }
          if (returnFocus) talentEl?.focus();
        };

        input.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            finish(true, true);
          } else if (e.key === 'Escape') {
            e.preventDefault();
            finish(false, true);
          }
        });
        input.addEventListener('blur', () => finish(true, false));

        anchor.hidden = true;
        talentInput = input;
        head.insertBefore(input, anchor.nextSibling);
        input.focus();
        input.select();
      }

      // ---- delete, with an undo toast -------------------------------------

      function deleteField() {
        const found = findBriefField(editor.state.doc, attrs.id);
        if (!found) return;
        const removed = found.node;
        const index = found.index;
        const title = attrs.title;

        if (!editor.commands.deleteBriefField(attrs.id)) return;
        showToast('success', `Deleted "${title}".`, {
          action: {
            label: 'Undo',
            onClick: () => {
              const tr = insertBriefFieldAtIndexTr(editor.state, removed, index);
              if (tr) editor.view.dispatch(tr);
            },
          },
        });
      }

      return {
        dom,
        contentDOM,
        update(updated: PMNode) {
          if (updated.type.name !== BRIEF_FIELD_NODE) return false;
          const next = normalizeBriefFieldAttrs(updated.attrs);
          if (!next.enabled) return false; // switched off → rebuild as hidden
          current = updated;
          attrs = next;
          syncData(next);
          syncCustomTag(next);
          renderChip(next);
          syncShootGlyph(next);
          syncTalent(next);
          if (!renameInput) titleEl.textContent = next.title;
          return true;
        },
        // The header row is ours: its mutations (chip re-render, rename input)
        // are none of ProseMirror's business. Anything inside contentDOM is
        // handled normally. Node.contains is true for the node itself, so
        // childList changes on contentDOM still get through.
        ignoreMutation(mutation: MutationRecord | { type: 'selection' }) {
          if (mutation.type === 'selection') return false;
          return !contentDOM.contains((mutation as MutationRecord).target);
        },
        // Keep the editor's keymap off the rename input and the chrome buttons.
        stopEvent(event: Event) {
          const target = event.target as Node | null;
          return !!target && head.contains(target);
        },
        destroy() {
          if (activeAnchor && (activeAnchor === chip || activeAnchor === dots)) {
            closeBriefMenu();
          }
        },
      };
    };
  },

  addCommands() {
    return {
      setBriefWarmups:
        (texts) =>
        ({ state, tr, dispatch }) =>
          applyBuilt(tr, dispatch, setBriefWarmupsTr(state, texts)),
      setBriefFieldAttrs:
        (id, patch) =>
        ({ state, tr, dispatch }) =>
          applyBuilt(tr, dispatch, patchBriefFieldAttrsTr(state, id, patch)),

      moveBriefField:
        (id, direction) =>
        ({ state, tr, dispatch }) =>
          applyBuilt(tr, dispatch, moveBriefFieldTr(state, id, direction)),

      moveBriefFieldTo:
        (id, targetId, placement) =>
        ({ state, tr, dispatch }) =>
          applyBuilt(tr, dispatch, moveBriefFieldToTr(state, id, targetId, placement)),

      addCustomBriefField:
        (input) =>
        ({ state, tr, dispatch, editor }) => {
          const ok = applyBuilt(tr, dispatch, addCustomBriefFieldTr(state, input));
          // The transaction puts the selection in the new field's empty
          // paragraph, but the click that ran this came from the Fields
          // drawer, so the caret only lands there once the editor takes focus
          // back. Deferred to the next task, by which time the transaction is
          // dispatched. A timeout rather than requestAnimationFrame: this has
          // to happen whether or not the page is painting, and ProseMirror's
          // own focus() rather than Tiptap's, which defers a further frame.
          if (ok && dispatch) {
            window.setTimeout(() => {
              if (!editor.isDestroyed) editor.view.focus();
            }, 0);
          }
          return ok;
        },

      deleteBriefField:
        (id) =>
        ({ state, tr, dispatch }) =>
          applyBuilt(tr, dispatch, deleteBriefFieldTr(state, id)),

      showBriefField:
        (id) =>
        ({ state, tr, dispatch }) =>
          applyBuilt(tr, dispatch, patchBriefFieldAttrsTr(state, id, { enabled: true })),

      hideBriefField:
        (id) =>
        ({ state, tr, dispatch }) =>
          applyBuilt(tr, dispatch, patchBriefFieldAttrsTr(state, id, { enabled: false })),
    };
  },
});

/**
 * Dispatch a built transaction through the command's own transaction, which is
 * the one Tiptap actually dispatches.
 *
 * The subtlety worth knowing about: the `state` Tiptap hands a command is a
 * *chainable* state whose `tr` getter returns the very transaction passed in.
 * So a builder that reaches for `state.tr` has already written its steps into
 * `tr`, and there is nothing to copy. Copying anyway would apply every step
 * twice — and, because `tr.steps` grows as the loop reads it, forever. The
 * copy branch is for a builder handed a real EditorState instead, which is how
 * briefField.test.ts drives them.
 *
 * Exported so that trap has a regression test.
 */
export function applyBuilt(
  tr: Transaction,
  dispatch: (() => void) | undefined,
  built: Transaction | null,
): boolean {
  if (!built) return false;
  if (!dispatch) return true;

  if (built !== tr) {
    for (const step of built.steps) tr.step(step);
    if (built.selectionSet) {
      const at = Math.min(built.selection.from, tr.doc.content.size);
      tr.setSelection(TextSelection.near(tr.doc.resolve(at)));
      tr.scrollIntoView();
    }
  }
  tr.setMeta(BRIEF_STRUCTURAL_META, true);
  return true;
}

// ---------------------------------------------------------------------------
// The portalled menu — one shared instance, opened under whichever chip or ⋯
// was clicked. Fixed-position and appended to <body> so it escapes the
// editor's overflow and stacking contexts, exactly as CellTypeChip's picker
// does.
// ---------------------------------------------------------------------------

interface BriefMenuItem {
  separator?: boolean;
  label?: string;
  gloss?: string;
  /** Right-aligned mono hint, e.g. "Custom only" on a built-in. */
  hint?: string;
  checked?: boolean;
  toggle?: boolean;
  disabled?: boolean;
  danger?: boolean;
  onSelect?: () => void;
}

interface BriefMenuOptions {
  head?: string;
  align?: 'left' | 'right';
  items: BriefMenuItem[];
}

const MENU_WIDTH = 268;
const MENU_GAP = 6;

let activeAnchor: HTMLElement | null = null;
let activeMenu: HTMLElement | null = null;
let detachMenu: (() => void) | null = null;

function openBriefMenu(anchor: HTMLElement, opts: BriefMenuOptions) {
  if (activeAnchor === anchor) {
    closeBriefMenu();
    return;
  }
  closeBriefMenu();

  const menu = document.createElement('div');
  menu.className = 'brief-menu';
  menu.setAttribute('role', 'menu');

  if (opts.head) {
    const headEl = document.createElement('div');
    headEl.className = 'brief-menu__head';
    headEl.textContent = opts.head;
    menu.appendChild(headEl);
  }

  for (const item of opts.items) {
    if (item.separator) {
      const sep = document.createElement('div');
      sep.className = 'brief-menu__sep';
      menu.appendChild(sep);
      continue;
    }
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'brief-menu__item';
    if (item.disabled) button.classList.add('brief-menu__item--disabled');
    if (item.danger && !item.disabled) button.classList.add('brief-menu__item--danger');
    if (item.checked) button.classList.add('brief-menu__item--checked');
    button.disabled = Boolean(item.disabled);
    button.setAttribute('role', item.checked === undefined ? 'menuitem' : item.toggle ? 'menuitemcheckbox' : 'menuitemradio');
    if (item.checked !== undefined) button.setAttribute('aria-checked', String(item.checked));

    const body = document.createElement('span');
    body.className = 'brief-menu__body';
    const name = document.createElement('span');
    name.className = 'brief-menu__name';
    name.textContent = item.label ?? '';
    body.appendChild(name);
    if (item.gloss) {
      const gloss = document.createElement('span');
      gloss.className = 'brief-menu__gloss';
      gloss.textContent = item.gloss;
      body.appendChild(gloss);
    }
    button.appendChild(body);

    if (item.hint || item.checked) {
      const hint = document.createElement('span');
      hint.className = 'brief-menu__hint';
      if (item.checked) hint.innerHTML = AUDIENCE_CHECK;
      else hint.textContent = item.hint ?? '';
      button.appendChild(hint);
    }

    button.addEventListener('mousedown', (e) => e.preventDefault());
    button.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (item.disabled) return;
      closeBriefMenu();
      item.onSelect?.();
    });
    menu.appendChild(button);
  }

  document.body.appendChild(menu);
  activeAnchor = anchor;
  activeMenu = menu;
  anchor.setAttribute('data-open', 'true');

  position();

  const options = Array.from(
    menu.querySelectorAll<HTMLButtonElement>('.brief-menu__item:not([disabled])'),
  );
  const checkedIdx = options.findIndex((b) =>
    b.getAttribute('role') !== 'menuitemcheckbox' && b.classList.contains('brief-menu__item--checked'),
  );
  options[Math.max(0, checkedIdx)]?.focus();

  function position() {
    const r = anchor.getBoundingClientRect();
    let left = opts.align === 'right' ? r.right - MENU_WIDTH : r.left;
    left = Math.min(left, window.innerWidth - MENU_WIDTH - 8);
    left = Math.max(8, left);
    const below = r.bottom + MENU_GAP;
    const menuH = menu.offsetHeight || 220;
    // Flip above when there is no room below.
    const top =
      below + menuH > window.innerHeight - 8 && r.top - MENU_GAP - menuH > 8
        ? r.top - MENU_GAP - menuH
        : below;
    menu.style.left = `${Math.round(left)}px`;
    menu.style.top = `${Math.round(top)}px`;
    menu.style.width = `${MENU_WIDTH}px`;
  }

  function onDocPointer(e: Event) {
    const target = e.target as Node;
    if (menu.contains(target) || anchor.contains(target)) return;
    closeBriefMenu();
  }
  function onKey(e: KeyboardEvent) {
    if (e.key === 'Escape') {
      e.preventDefault();
      closeBriefMenu();
      anchor.focus?.();
      return;
    }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const list = Array.from(
        menu.querySelectorAll<HTMLButtonElement>('.brief-menu__item:not([disabled])'),
      );
      const idx = list.findIndex((b) => b === document.activeElement);
      const nextIdx =
        e.key === 'ArrowDown'
          ? (idx + 1) % list.length
          : (idx - 1 + list.length) % list.length;
      list[nextIdx]?.focus();
    }
  }
  document.addEventListener('mousedown', onDocPointer, true);
  document.addEventListener('keydown', onKey, true);
  window.addEventListener('scroll', position, true);
  window.addEventListener('resize', position);

  detachMenu = () => {
    document.removeEventListener('mousedown', onDocPointer, true);
    document.removeEventListener('keydown', onKey, true);
    window.removeEventListener('scroll', position, true);
    window.removeEventListener('resize', position);
  };
}

function closeBriefMenu() {
  detachMenu?.();
  detachMenu = null;
  activeAnchor?.removeAttribute('data-open');
  activeMenu?.remove();
  activeAnchor = null;
  activeMenu = null;
}
